import { Clock } from "@tamagui/lucide-icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { Heading, Paragraph, ScrollView, XStack, YStack } from "@/components";
import Ellipses from "@/components/animated/Ellipses";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import SmsConsentDialog from "@/conversation/SmsConsentDialog";
import { isClinicRequestExpiredError } from "@/utils/clinicRequestError";

type AsyncVisitProps = {
  onClose?: () => void;
  clinicRequestId?: string;
  handleComplete?: () => Promise<unknown>;
  onContinue?: () => void;
  onClinicRequestExpired?: () => void;
};

const AsyncVisit = ({
  onClose,
  clinicRequestId,
  handleComplete,
  onContinue,
  onClinicRequestExpired,
}: AsyncVisitProps) => {
  const hasStartedRef = useRef(false);

  const { data: userProfile } = useGetUserProfile();
  const { mutate: updateConsents, isPending: isConsentUpdating } =
    useUpdateConsents();
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const { value: smsConsentNudgeEnabled } = useFeatureFlag(
    "sms-consent-nudge",
    false,
  );

  const hasConsentedToSms =
    userProfile?.consents?.[ConsentType.sms_consent_to_treat] === true;
  // Dialog is being replaced by smsConsentNudge when the feature flag is enabled
  const shouldShowConsentDialog = !hasConsentedToSms && !smsConsentNudgeEnabled;
  const shouldShowConsentDialogRef = useRef(shouldShowConsentDialog);
  useEffect(() => {
    shouldShowConsentDialogRef.current = shouldShowConsentDialog;
  }, [shouldShowConsentDialog]);

  const advance = useCallback(() => {
    onContinue?.();
  }, [onContinue]);

  useEffect(() => {
    if (hasStartedRef.current || !clinicRequestId) return;
    hasStartedRef.current = true;
    handleComplete?.()
      .then(() => {
        if (shouldShowConsentDialogRef.current) {
          setShowConsentDialog(true);
        } else {
          advance();
        }
      })
      .catch((error) => {
        if (isClinicRequestExpiredError(error)) {
          onClinicRequestExpired?.();
        } else {
          onClose?.();
        }
      });
  }, [
    clinicRequestId,
    handleComplete,
    advance,
    onClose,
    onClinicRequestExpired,
  ]);

  const submitConsent = (consented: boolean) => {
    updateConsents(
      {
        data: {
          consents: {
            [ConsentType.sms_consent_to_treat]: consented,
          },
          source: "async-visit",
        },
      },
      {
        // Advance on either outcome: the clinic request already succeeded, and the
        // global mutation error toast surfaces the failure. Leaving the consent
        // unset is fine — we'll re-prompt next time.
        onSettled: () => {
          setShowConsentDialog(false);
          advance();
        },
      },
    );
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) return;
    // Dismissed without choosing — do not write a consent value, just proceed.
    setShowConsentDialog(false);
    advance();
  };

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          flex: 1,
          paddingHorizontal: "$200",
          paddingBottom: "$400",
        }}
      >
        <YStack flex={1} gap="$150">
          <Heading size="$lg">
            Sending your request
            <Ellipses />
          </Heading>
          <Paragraph size="$sm" color="$textSecondary">
            Please wait while we submit your request.
          </Paragraph>
        </YStack>
      </ScrollView>
      <SmsConsentDialog
        open={showConsentDialog}
        onOpenChange={handleDialogOpenChange}
        onConsent={() => submitConsent(true)}
        onDecline={() => submitConsent(false)}
        isLoading={isConsentUpdating}
        title="Want a text when it's ready?"
        description={
          <YStack gap="$200">
            <Paragraph>
              Your request is now with our clinical team and will be reviewed by
              a licensed clinician soon.
            </Paragraph>
            <XStack gap="$150" alignItems="center">
              <Clock size="$300" color="$textSecondary" flexShrink={0} />
              <Paragraph>It may take up to an hour</Paragraph>
            </XStack>
            <Paragraph>
              However, we can text you as soon as there&apos;s an update if you
              consent to receive treatment over SMS.
            </Paragraph>
          </YStack>
        }
        showConsentFeatures={false}
      />
    </>
  );
};

export default AsyncVisit;
